import { selectBreadcrumbs, selectIsFilterActive, selectSearchQuery } from "~/features/search/redux/searchSelectors";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { type BreadcrumbData, BreadcrumbType } from "~/utils/models";
import {
  clearSearchValueAction,
  removeBreadcrumb,
  toggleCampaignOnlyResultsThunk,
} from "~/features/search/redux/actions";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

export interface AppliedFilterData {
  key: string;
  onClick: () => void;
  value: ReactNode;
}

const formatter = NF.getFormatter();

export const useSelectAppliedFilters = () => {
  const { t } = useTranslation();
  const breadcrumbs = useSelector(selectBreadcrumbs);

  const searchValue = useSelector(selectSearchQuery);

  const searchPageType = useSearchPageType();

  const dispatch = useDispatch();
  const { onRemoveBreadcrumb, clearSearchValue, clearSalesFilter } = useMemo(
    () => ({
      onRemoveBreadcrumb: (breadcrumb: BreadcrumbData) => dispatch(removeBreadcrumb(breadcrumb)),
      clearSearchValue: () => dispatch(clearSearchValueAction(searchPageType)),
      clearSalesFilter: () => dispatch(toggleCampaignOnlyResultsThunk(false)),
    }),
    [searchPageType],
  );

  const appliedFilters: AppliedFilterData[] = [];

  breadcrumbs
    ?.filter((breadcrumb) => breadcrumb.name !== "" && breadcrumb.value !== "")
    .forEach((breadcrumb) => {
      if (breadcrumb.type === BreadcrumbType.FACET) {
        appliedFilters.push({
          key: `${breadcrumb.facetCode}-${breadcrumb.facetValueCode}`,
          onClick: () => onRemoveBreadcrumb(breadcrumb),
          value: `${breadcrumb.name}: ${breadcrumb.value}`,
        });
      } else {
        const minMaxKey = `searchPage.refinement.value.label.${breadcrumb.valueType}`;
        appliedFilters.push({
          key: `${breadcrumb.code}-${breadcrumb.valueType}`,
          onClick: () => onRemoveBreadcrumb(breadcrumb),
          value: `${breadcrumb.name} ${t(minMaxKey)}: ${formatter.format(breadcrumb.value)}`,
        });
      }
    });

  const isSalesCampaignPage = searchPageType === SearchPageType.Campaign;
  if (isSalesCampaignPage && searchValue !== "") {
    appliedFilters.push({
      key: searchValue,
      onClick: () => clearSearchValue(),
      value: t("feature.salesCampaign.facet.searchQuery", { query: searchValue }),
    });
  }

  const isSalesFilterActive = useSelector(selectIsFilterActive);
  if (!isSalesCampaignPage && isSalesFilterActive) {
    appliedFilters.push({
      key: searchValue,
      onClick: () => clearSalesFilter(),
      value: t("feature.salesCampaign.specialDealPrice"),
    });
  }

  return appliedFilters;
};
